import { Mui } from '@osu/react-ui';
import { replaceContentPlaceholders } from '../../utils/stepHelpers';
import ExternalLink from '../../../Common/components/ExternalLink';
import HighlightBox from '../../../Common/styled/HighlightBox';

/**
 * Helper component to render contact information with proper React components
 * instead of dangerouslySetInnerHTML
 */
const ContactInfoText = ({ text }) => {
  if (!text) return null;

  // Split text by email and phone patterns to create proper React elements
  const contactMap = {
    'honors-scholars@osu.edu': 'mailto:honors-scholars@osu.edu',
    '(614) 292-3135': 'tel:614-292-3135',
    'askabuckeye@osu.edu': 'mailto:askabuckeye@osu.edu',
    '(614) 292-3980': 'tel:614-292-3980'
  }
  const pattern = Object.keys(contactMap).join('|').replace(/[()]/g, '\\$&')
  const parts = text.split(new RegExp(`(${pattern})`, 'g'))
  const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
  const phoneRegex = /\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;

  return (
    <>
      {parts.map((part, index) => {
        let externalLinkProps = {
          href: '',
          color: 'inherit',
          underline: 'hover'
        }
        if(contactMap[part]) {
          externalLinkProps.href = contactMap[part]
        } else if (emailRegex.test(part)) {
          externalLinkProps.href = `mailto:${part}`
        } else if (phoneRegex.test(part)) {
          externalLinkProps.href = `tel:${part.replaceAll(" ", "").replaceAll("(", "").replaceAll(")", "")}`
        }
        
        if(externalLinkProps.href) {
          return <ExternalLink
            key={index}
            {...externalLinkProps}
          >
            {part}
          </ExternalLink>
        }
      return part
  })}
  </>
  );
};

/**
 * DecisionOutcome component displays different decision outcomes with appropriate actions
 * Receives pre-processed content and actions as props from step building logic
 * @param {Object} props
 * @param {Object} [props.applicationData] - Application data for dynamic content
 * @param {Object} props.stepContent - Pre-processed content from step building
 * @param {Array} props.stepActions - Pre-processed actions from step building
 * @param {function} [props.onAction] - Action handler callback
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.isAdminView] - Whether component is being rendered in admin view mode
 */
function DecisionOutcome({
  applicationData = {},
  stepContent, // Pre-processed content from step building
  stepActions, // Pre-processed actions from step building
  onAction,
  className = '',
  isAdminView = false
}) {

  // Use the pre-processed content and actions passed as props
  const decisionContent = stepContent || {};
  const decisionActions = stepActions || [];
  // Prepare content replacements for dynamic values
  const replacements = {
    deadline: applicationData?.selfService?.deadline ||
      `May 1, ${new Date().getFullYear() + 1}`
  };

  // Replace placeholders in content
  const processedContent = {
    title: replaceContentPlaceholders(decisionContent.title, replacements),
    description: replaceContentPlaceholders(decisionContent.description, replacements),
    message: replaceContentPlaceholders(decisionContent.message, replacements),
    additionalInfo: replaceContentPlaceholders(decisionContent.additionalInfo, replacements),
    contactInfo: replaceContentPlaceholders(decisionContent.contactInfo, replacements)
  };



  const handleAction = (action) => {
    // Prevent decline actions in admin view
    if (isAdminView && action.type === 'decline-honors') {
      console.log('Decline action blocked in admin view mode');
      return;
    }

    // Handle actions through the callback
    if (onAction) {
      onAction(action.type, action);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'info': return 'info';
      case 'warning': return 'warning';
      default: return 'info';
    }
  };

  return (
    <div
      className={`decision-outcome ${className}`}
      role="region"
      aria-labelledby="decision-title"
    >

      {/* Congratulations or Main Message */}
      {(decisionContent.congratsMessage || decisionContent.mainMessage) && (
        <Mui.Typography
          variant="h5"
          component="h2"
          id="decision-title"
          sx={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            mb: 2,
            color: decisionContent.severity === 'success' ? 'success.main' : 'text.primary'
          }}
        >
          {replaceContentPlaceholders(decisionContent.congratsMessage || decisionContent.mainMessage, replacements)}
        </Mui.Typography>
      )}

      {/* Main description */}
      {processedContent.description && (
        <Mui.Typography variant="body1" sx={{ mb: 2 }}>
          {processedContent.description}
        </Mui.Typography>
      )}

      {/* Message in alert box */}
      {processedContent.message && (
        <Mui.Alert severity={getSeverityColor(decisionContent.severity)} sx={{ mb: 3 }}>
          <Mui.Typography variant="body1">
            {processedContent.message}
          </Mui.Typography>
        </Mui.Alert>
      )}

      {/* Additional info */}
      {processedContent.additionalInfo && (
        <Mui.Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {processedContent.additionalInfo}
        </Mui.Typography>
      )}

      {/* Contact information */}
      {processedContent.contactInfo && (
          <HighlightBox variant="body2">
            <ContactInfoText text={processedContent.contactInfo} />
          </HighlightBox>
      )}

      {/* Action buttons */}
      {decisionActions && decisionActions.length > 0 && (
        <Mui.Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {decisionActions.map((action, index) => {
            // Check if this is a decline button in admin view
            const isDeclineButton = action.type === 'decline-honors';
            const shouldDisable = isAdminView && isDeclineButton;

            return (
              <Mui.Button
                key={index}
                variant={action.variant || 'contained'}
                color={action.color || 'primary'}
                size="large"
                onClick={() => handleAction(action)}
                disabled={shouldDisable}
                sx={{
                  minWidth: '150px',
                  ...(shouldDisable && {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    '&:hover': {
                      backgroundColor: 'action.disabled'
                    }
                  })
                }}
                aria-label={`${action.label} for decision`}
                title={shouldDisable ? 'Actions are disabled in admin view mode' : undefined}
              >
                {action.label}
              </Mui.Button>
            );
          })}
        </Mui.Box>
      )}
    </div>
  );
}

export default DecisionOutcome;